import { useRef } from 'react';

import { InsightsGetValuesOptions } from 'contextProviders/InsightsContentProvider';
import { InsightsGadgetValues } from 'contextProviders/InsightsContentProvider/viewTypes';

import buildMatrixValuesDigest from './buildMatrixValuesDigest';
import useRegisterValues from './useRegisterValues';

// Makes a matrix-shaped gadget's rendered grid available to the AI Insights Chat.
//
// Called from useMetricMatrix, which is the data hook for the gadget types that do
// NOT go through useV5ChartData: matrix, stackedBar, stackedArea, heatmap and
// sankey. Matrix is the important one — a table of lanes or drivers with their
// metrics is exactly what "why is BECFRA so far behind in loads" is about, and
// before this the agent could name the gadget but had none of its numbers.
const useRegisterInsightsMatrixValues = ({
  chartDef,
  rows,
  columnDefs,
  isLoading,
}: {
  chartDef: V5ChartDefinition;
  rows: (MatrixRowData | MatrixComparisonRowData)[];
  columnDefs: MatrixColDef[];
  isLoading: boolean;
}): void => {
  // When the current numbers arrived, so the digest can say "as displayed at …".
  const receivedAtRef = useRef<string>('');
  const previousRowsRef = useRef<
    (MatrixRowData | MatrixComparisonRowData)[] | undefined
  >(undefined);
  if (rows !== previousRowsRef.current) {
    previousRowsRef.current = rows;
    receivedAtRef.current = new Date().toISOString();
  }

  const latestRef = useRef({ chartDef, rows, columnDefs, isLoading });
  latestRef.current = { chartDef, rows, columnDefs, isLoading };

  useRegisterValues({
    source: 'matrix',
    getValues: (opts?: InsightsGetValuesOptions): InsightsGadgetValues => {
      const current = latestRef.current;
      // Mid-refetch: the grid still holds the previous filters' rows.
      if (current.isLoading) {
        return { isLoading: true };
      }
      return buildMatrixValuesDigest({
        chartDef: current.chartDef,
        rows: current.rows,
        columnDefs: current.columnDefs,
        asOf: receivedAtRef.current,
        pinnedLabels: opts?.pinnedLabels,
      });
    },
  });
};

export default useRegisterInsightsMatrixValues;
